import {
  useEffect,
  useMemo,
  MouseEvent,
  useCallback,
  KeyboardEvent,
  useState,
  memo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FormikProps, useFormik } from 'formik';
import styled from '@emotion/styled';
import { Loader } from '@kep-ui-kit/ui-components';
import { Alert24Icon } from '@kep-ui-kit/ui-icons';

import {
  StyledContainer,
  StyledSubTitle,
  StyledButton,
  StyledText,
  StyledFormGroup,
  StyledInput,
} from '@selfserve/react-ui';
import {
  useStorage,
  storage,
  storageKeys,
  useCountryList,
  usePrevious,
} from '@selfserve/react-hooks';
import {
  apiStatuses,
  couponDuration,
  currencyTypes,
  currencySigns,
  discountTypes,
  submitByKeyboard,
  billingCycles,
  IResponse,
  handleGtmErrors,
  encodeUri, trackButtonClickEvent, ButtonType, ApplicationFeatures
} from '@selfserve/react-utils';

import {
  PlanPayload,
  useCalculateSummaryRequest,
  useCalculateSummaryBySubscriptionIdRequest,
  useApplyCouponRequest,
  useRemoveCouponRequest,
  useCouponInfoRequest,
} from '../data';
import { SummaryAddons, SummaryPlan, SummaryPriceContainer } from '../ui';
import { SummaryPromoCodes } from '../ui/summary-promocodes';

const StyledSummaryContainer = styled(StyledContainer)(
  ({ theme }: { theme?: any }) => ({
    marginBottom: theme.spacing(4),
  })
);

const StyledPromotionalCodeContainer = styled(StyledContainer)(
  ({ theme }: { theme?: any }) => ({
    marginBottom: theme.spacing(2),
  })
);

const StyledApplyButton = styled(StyledButton)(
  ({ theme }: { theme?: any }) => ({
    marginLeft: theme.spacing(2),
    height: 'fit-content',
  })
);

export const StyledPriceContainer = styled.div(
  ({ withLine, theme }: { theme?: any; withLine?: boolean }) => ({
    marginBottom: theme.spacing(2),
    ...(withLine && {
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.grey['400']}`,
    }),
  })
);

export const StyledPricePair = styled.div(
  ({ secondary, theme }: { theme?: any; secondary?: boolean }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: theme.typography.pxToRem(17),
    ...(secondary && {
      lineHeight: theme.typography.pxToRem(20),
      color: theme.palette.grey['300'],
    }),
  })
);

export const StyledPlanRenewText = styled(StyledText)(
  ({ theme }: { theme?: any }) => ({
    fontSize: theme.typography.pxToRem(13),
    lineHeight: theme.typography.pxToRem(16),
    margin: `${theme.spacing(2)} 0`,
  })
);

const StyledErrorContainer = styled('p')(({ theme }: { theme?: any }) => ({
  width: '100%',
  position: 'relative',
  padding: theme.spacing(1),
  borderRadius: '4px',
  paddingLeft: '36px',
  fontSize: theme.typography.pxToRem(14),
  lineHeight: theme.typography.pxToRem(16),
  color: theme.palette.danger.main,
  background: theme.palette.grey['700'],
  margin: `0 0 ${theme.spacing(2)}`,
}));

const StyledErrorIcon = styled(Alert24Icon)(({ theme }: { theme?: any }) => ({
  position: 'absolute',
  left: '9px',
  top: '50%',
  marginTop: '-12px',
}));

const StyledSafeChargeLogo = styled.img(({ theme }: { theme?: any }) => ({
  maxWidth: '60px',
  maxHeight: '32px',
  marginRight: theme.spacing(2),
}));

export const StyledLoader = styled(Loader)`
  margin-left: -20px;
`;

interface PromotionalCodeProps
  extends Pick<
    FormikProps<FormValues>,
    | 'values'
    | 'handleBlur'
    | 'handleChange'
    | 'touched'
    | 'errors'
    | 'setFieldError'
  > {
  promotionalButtonId?: string;
  onClick: () => void;
  disabled: boolean;
}

const PromotionalCode = memo(
  ({
    handleChange,
    handleBlur,
    setFieldError,
    values,
    touched,
    errors,
    promotionalButtonId,
    onClick,
    disabled,
  }: PromotionalCodeProps) => {
    const { t } = useTranslation();

    const onkeydownHandle = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (submitByKeyboard(event)) onClick();
      },
      [onClick]
    );

    return (
      <StyledPromotionalCodeContainer fitHeight>
        <StyledSubTitle as="span">
          {t('common.promotional-code')}
        </StyledSubTitle>
        <StyledFormGroup row withoutMargin>
          <StyledFormGroup withoutMargin>
            <StyledInput
              id=""
              name="promotionalCode"
              onChange={(e) => {
                handleChange(e);
                if (errors.promotionalCode) {
                  setFieldError('promotionalCode', '');
                }
              }}
              onBlur={handleBlur}
              onKeyDown={onkeydownHandle}
              value={values.promotionalCode}
              error={touched.promotionalCode ? !!errors.promotionalCode : false}
              inputProps={{ 'aria-label': t('aria.promotional-code') }}
              disabled={disabled}
              fullWidth
            />
            {errors.promotionalCode && (
              <p className="p-error">{errors.promotionalCode}</p>
            )}
          </StyledFormGroup>
          <StyledApplyButton
            id={promotionalButtonId}
            variant="borderless"
            type="button"
            onClick={onClick}
            disabled={disabled}
            isBorderless
            withoutMargin
          >
            {t('common.apply')}
          </StyledApplyButton>
        </StyledFormGroup>
      </StyledPromotionalCodeContainer>
    );
  }
);

type SummaryItem = {
  quantity: number | undefined;
  price: number | undefined;
  name: string | undefined;
};

type Plan = SummaryItem & { visible: boolean | undefined };

export type Addon = SummaryItem & {
  secondaryName: string | undefined;
  key: string | undefined;
};

export interface ISummary {
  plan: Plan;
  addons: Addon[];
}

export interface PromoCode {
  name?: string;
  value?: string;
  id: string;
  secondaryName?: string;
}

interface Props {
  payload: PlanPayload;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  buttonText?: string;
  buttonId: string;
  isLoading?: boolean;
  disabled?: boolean;
  withoutDisabledStyles?: boolean;
  isCheckout?: boolean;
  promotionalButtonId?: string;
  summaryDescription?: string;
  countryCode?: string;
  handleRemoveAddon?: (key: string) => () => void;
  handleError?: (text: string) => void;
  onAmountChange?: (amount: number) => void;
  apiError?: string;
  packageName?: string;
  summary: ISummary;
  changeBillingAddressResponse?: IResponse<null>;
}

interface FormValues {
  promotionalCode: string;
}

export const Summary = memo(
  ({
    payload,
    summary: { plan, addons },
    handleError,
    handleRemoveAddon,
    onAmountChange,
    buttonText,
    summaryDescription,
    buttonId,
    isLoading,
    promotionalButtonId,
    disabled,
    withoutDisabledStyles,
    changeBillingAddressResponse,
    isCheckout,
    countryCode,
    onClick,
    apiError,
    packageName,
  }: Props) => {
    const { t } = useTranslation();
    const { hash, subscriptionId } = useParams();
    const { EUVATCountryCodes } = useCountryList();
    const [description, setDescription] = useStorage(
      storage.SESSION,
      storageKeys.SUMMARY_DESCRIPTION,
      summaryDescription
    );
    const [planId] = useStorage(storage.SESSION, storageKeys.PROMO_PLAN_ID);
    const [promoCodesToApply] = useStorage<string[]>(
      storage.SESSION,
      storageKeys.PROMO_CODES_APPLY
    );
    const [appliedPromoCodes, setAppliedPromoCodes] = useStorage<PromoCode[]>(
      storage.SESSION,
      storageKeys.PROMO_CODES,
      []
    );
    const [promoCodeDisabled, setPromoCodeDisabled] = useState(false);
    const [removeId, setRemoveId] = useState('');
    const [amount, setAmount] = useState(0);
    const chargeOptionTitle =
      payload?.billing_cycle?.toLocaleLowerCase() === billingCycles.MONTHLY
        ? t('common.monthly-charges')
        : t('common.annual-charges');
    const formik: FormikProps<FormValues> = useFormik<FormValues>({
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        promotionalCode: promoCodesToApply ? promoCodesToApply[0] || '' : '',
      },
      onSubmit: () => undefined,
    });
    const {
      errors,
      values,
      handleChange,
      handleBlur,
      touched,
      setFieldValue,
      setFieldError,
    } = formik;
    const calculateByHashResponse = useCalculateSummaryRequest(hash);
    const calculateBySubscriptionIdResponse =
      useCalculateSummaryBySubscriptionIdRequest(subscriptionId);
    const {
      mutateAsync: handleCalculateSummaryRequest,
      data: calculateResponse,
      isLoading: isCalculateLoading,
    } = subscriptionId
      ? calculateBySubscriptionIdResponse
      : calculateByHashResponse;
    const {
      mutateAsync: handleApplyCouponRequest,
      data: applyCouponResponse,
      isLoading: isApplyCouponLoading,
    } = useApplyCouponRequest(planId, encodeUri(values.promotionalCode));
    const { mutateAsync: handleRemoveCouponRequest } = useRemoveCouponRequest(
      planId,
      removeId
    );
    const {
      data: couponInfoResponse,
      isLoading: isCouponInfoLoading,
      refetch: fetchCouponInfo,
    } = useCouponInfoRequest(encodeUri(values.promotionalCode), false);
    const { data, status } = calculateResponse || {};
    const { amountDue, secureAmount, couponDiscount, taxes } = data || {};
    const { taxRate, taxableAmount, taxAmount } = taxes || {};
    const { plan: payloadPlan, addons: payloadAddons } = payload;
    const isCalculating = useMemo(
      () => isCalculateLoading || isApplyCouponLoading || isCouponInfoLoading,
      [isCalculateLoading, isApplyCouponLoading, isCouponInfoLoading]
    );
    const addonsNamesMemo = useMemo(
      () =>
        payloadAddons
          ?.map(({ name }) => name as string)
          .reduce((a, b) => a + b, ''),
      [payload.addons]
    );
    const addonQuantitiesMemo = useMemo(
      () =>
        payloadAddons
          ?.map(({ quantity }) => quantity as number)
          .reduce((a, b) => a + b, 0),
      [payload.addons]
    );
    const planPrice = useMemo(
      () => (plan.price as number) * (plan.quantity as number),
      [plan]
    );
    const absolutePrice = useMemo(
      () =>
        addons
          .map(
            ({ price, quantity }) => (price as number) * (quantity as number)
          )
          .reduce((a: number, b: number) => a + b, planPrice),
      [addons, planPrice]
    );
    const isWithTaxes = useMemo(
      () => !!(isCheckout && taxAmount && countryCode),
      [taxAmount, countryCode, isCheckout]
    );
    const absoluteTax = useMemo(
      () => absolutePrice * ((taxRate as number) / 100),
      [absolutePrice, taxRate]
    );
    const absoluteCharge = useMemo(
      () => (isWithTaxes ? absolutePrice + absoluteTax : absolutePrice),
      [absolutePrice, absoluteTax, isWithTaxes]
    );
    const todaysCharge = useMemo(
      () => (isWithTaxes ? amount : taxableAmount || amount),
      [amount, taxableAmount, isWithTaxes]
    );
    const isEUVATCountry = useMemo(
      () => EUVATCountryCodes.includes(countryCode || ''),
      [countryCode]
    );
    const prevAbsoluteCharge = usePrevious(absoluteCharge);
    const prevTodaysCharge = usePrevious(todaysCharge);

    const applyPromoCode = useCallback(() => {
      const { promotionalCode } = values;
      if (promotionalCode) {
        handleCalculateSummaryRequest({
          ...payload,
          coupon_ids: [encodeUri(values.promotionalCode)],
        });
      }
      trackButtonClickEvent(
        ButtonType.Create,
        'apply_promotional_code',
        promotionalCode,
        null,
        ApplicationFeatures.SUBSCRIPTION_FLOW
      );
    }, [payload, handleCalculateSummaryRequest, values]);

    const handleRemovePromoCode = useCallback(
      (removeId: string) => () => {
        const newValue = appliedPromoCodes.filter(({ id }) => removeId !== id);
        setAppliedPromoCodes(newValue);
        handleCalculateSummaryRequest({
          ...payload,
          coupon_ids: newValue.map(({ id }) => id),
        });
        setPromoCodeDisabled(false);
        setRemoveId(removeId);
      },
      [
        handleCalculateSummaryRequest,
        setAppliedPromoCodes,
        payload,
        appliedPromoCodes,
      ]
    );

    const getCouponDuration = useCallback(() => {
      const { duration } = couponInfoResponse?.data || {};
      switch (duration?.durationType) {
        case couponDuration.FOREVER:
          return t('summary.all-time');
        case couponDuration.ONE_TIME:
          return t('summary.this-purchase');
        case couponDuration.LIMITED_PERIOD: {
          const amount = duration?.limitedPeriodAmount || 0;
          const period = duration?.limitedPeriodTime
            ? duration?.limitedPeriodTime + (amount > 1 ? 's' : '')
            : '';
          return `${amount || ''} ${period ? t(`summary.${period}`) : ''}`;
        }
        default:
          return '';
      }
    }, [couponInfoResponse, t]);

    const getCurrencySign = useCallback(() => {
      const { discount } = couponInfoResponse?.data || {};
      switch (discount?.discountCurrency) {
        default:
        case currencyTypes.USD:
          return currencySigns.USD;
        case currencyTypes.EUR:
          return currencySigns.EUR;
      }
    }, [couponInfoResponse]);

    const getCouponDescription = useCallback(() => {
      const { discount } = couponInfoResponse?.data || {};
      let amount = discount?.discountAmount?.toString();
      switch (discount?.discountType) {
        case discountTypes.PERCENTAGE:
          amount += '%';
          break;
        case discountTypes.FIXED:
          amount += getCurrencySign();
          break;
      }
      return `${amount} ${t('summary.discount-for')} ${getCouponDuration()}`;
    }, [couponInfoResponse, getCouponDuration, getCurrencySign, t]);

    const thousandSeperator = useCallback(
      (amount: number) =>
        amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      []
    );

    const updatePromoCodesAmounts = useCallback(() => {
      const { data: couponInfo } = couponInfoResponse || {};
      if (couponInfo) {
        const currentCoupon = couponDiscount?.find(
          ({ coupon_id }) => coupon_id === couponInfo?.remote_ref_id
        );
        const promoCodeItem = {
          name: couponInfo?.invoice_name || '',
          value: `-${getCurrencySign()}${currentCoupon?.amount}`,
          id: currentCoupon?.coupon_id || '',
          secondaryName: getCouponDescription(),
        };
        setAppliedPromoCodes([promoCodeItem]);
        setFieldValue('promotionalCode', '');
        setPromoCodeDisabled(true);
      }
    }, [
      couponDiscount,
      couponInfoResponse,
      getCouponDescription,
      getCurrencySign,
    ]);

    useEffect(() => {
      if (status === apiStatuses.CREATED) {
        if (typeof amountDue === 'number') {
          setAmount(amountDue);
        }
        if (
          typeof secureAmount === 'number' &&
          typeof onAmountChange === 'function'
        ) {
          // for payment intent
          onAmountChange(secureAmount);
        }
      }
    }, [calculateResponse]);

    useEffect(() => {
      if (payload.plan?.name) {
        const couponIds = appliedPromoCodes?.length
          ? appliedPromoCodes.map(({ id }) => id)
          : promoCodesToApply;
        if (couponIds?.length) {
          setPromoCodeDisabled(true);
        }
        handleCalculateSummaryRequest({
          ...payload,
          coupon_ids: couponIds || [],
        }).then(({ data }) => {
          if (appliedPromoCodes?.length) {
            setAppliedPromoCodes(
              appliedPromoCodes?.map((promoCode) => {
                const currentCoupon = data?.couponDiscount?.find(
                  ({ coupon_id }) => coupon_id === promoCode.id
                );
                return {
                  ...promoCode,
                  value: currentCoupon
                    ? `${promoCode.value?.slice(0, 2)}${currentCoupon.amount}`
                    : promoCode.value,
                };
              })
            );
          }
        });
      }
    }, [
      payloadPlan?.name,
      payloadPlan?.quantity,
      payload.billing_cycle,
      payloadAddons?.length,
      addonsNamesMemo,
      addonQuantitiesMemo,
    ]);

    useEffect(() => {
      if (
        changeBillingAddressResponse?.status === apiStatuses.CREATED &&
        payload.plan?.name
      ) {
        const couponIds = appliedPromoCodes?.length
          ? appliedPromoCodes.map(({ id }) => id)
          : promoCodesToApply;
        if (couponIds?.length) {
          setPromoCodeDisabled(true);
        }
        handleCalculateSummaryRequest({
          ...payload,
          coupon_ids: couponIds || [],
        });
      }
    }, [changeBillingAddressResponse]);

    useEffect(() => {
      if ((!payload || !plan || !addons) && handleError) {
        handleError(t('common.purchase-error'));
      }
    }, [payload, plan, addons]);

    useEffect(() => {
      if (isCheckout && status === apiStatuses.NOT_ACCEPTABLE) {
        setFieldError('promotionalCode', t('errors.promo-code'));
      }
      if (
        handleError &&
        (status === apiStatuses.UNAUTHORIZED ||
          status === apiStatuses.FORBIDDEN)
      ) {
        handleError(t('common.purchase-error'));
      }
    }, [isCheckout, calculateResponse]);

    useEffect(() => {
      if (couponDiscount && couponDiscount.length > appliedPromoCodes?.length) {
        fetchCouponInfo();
        handleApplyCouponRequest({});
        setFieldError('promotionalCode', '');
      }
    }, [
      couponDiscount,
      appliedPromoCodes,
      handleApplyCouponRequest,
      fetchCouponInfo,
      setFieldValue,
    ]);

    useEffect(() => {
      if (
        couponDiscount &&
        couponDiscount.length > appliedPromoCodes?.length &&
        applyCouponResponse?.status === apiStatuses.CREATED &&
        couponInfoResponse?.status === apiStatuses.SUCCESS
      ) {
        updatePromoCodesAmounts();
      }
      switch (applyCouponResponse?.status) {
        case apiStatuses.UNAUTHORIZED:
        case apiStatuses.SERVER_ERROR:
        case apiStatuses.FORBIDDEN: {
          const { promotionalCode } = values;
          const newValue = appliedPromoCodes.filter(
            ({ id }) => promotionalCode !== id
          );
          setAppliedPromoCodes(newValue);
          handleCalculateSummaryRequest({
            ...payload,
            coupon_ids: newValue.map(({ id }) => encodeUri(id)),
          });
          break;
        }
      }
    }, [
      applyCouponResponse,
      couponInfoResponse,
      values,
      couponDiscount,
      appliedPromoCodes,
      handleCalculateSummaryRequest,
      setAppliedPromoCodes,
      payload,
      setFieldValue,
    ]);

    useEffect(() => {
      if (removeId) {
        handleRemoveCouponRequest({});
        setRemoveId('');
      }
    }, [handleRemoveCouponRequest, removeId]);

    useEffect(() => {
      if (summaryDescription && summaryDescription !== description) {
        setDescription(summaryDescription);
      }
    }, [summaryDescription]);

    useEffect(() => {
      if (
        typeof absoluteCharge === 'number' &&
        typeof todaysCharge === 'number'
      ) {
        if (
          (!isNaN(prevAbsoluteCharge as number) && isNaN(absoluteCharge)) ||
          (!isNaN(prevTodaysCharge as number) && isNaN(todaysCharge))
        ) {
          handleGtmErrors(20002, { packageName: packageName as string });
        }
      }
    }, [absoluteCharge, todaysCharge]);

    return (
      <StyledSummaryContainer>
        {isCheckout && (
          <PromotionalCode
            errors={errors}
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldError={setFieldError}
            touched={touched}
            promotionalButtonId={promotionalButtonId}
            onClick={applyPromoCode}
            disabled={promoCodeDisabled || isCalculating}
          />
        )}
        <StyledSubTitle className="sub-header" as="span">
          {t('common.summary')}
        </StyledSubTitle>
        {plan.visible && (
          <SummaryPlan
            withLine={
              !addons.length && !appliedPromoCodes?.length && !isWithTaxes
            }
            isLoading={isCalculating}
            price={thousandSeperator(planPrice)}
            name={plan.name as string}
            quantity={plan.quantity as number}
          />
        )}
        <SummaryAddons
          withLine={!appliedPromoCodes?.length && !isWithTaxes}
          addons={addons}
          isLoading={isCalculating}
          isCheckout={isCheckout}
          handleRemoveAddon={handleRemoveAddon}
        />
        <SummaryPromoCodes
          withLine={!isWithTaxes}
          promoCodes={appliedPromoCodes || []}
          exceptionCodes={promoCodesToApply}
          isLoading={isCalculating}
          handleRemovePromoCode={handleRemovePromoCode}
        />
        {isWithTaxes && (
          <SummaryPriceContainer
            isLoading={isCalculating}
            price={thousandSeperator(absoluteTax)}
            name={t('common.taxes')}
            hideSecondaryPart
            withLine
          />
        )}
        <SummaryPriceContainer
          isLoading={isCalculating}
          price={thousandSeperator(absoluteCharge || 0)}
          name={chargeOptionTitle}
          hideSecondaryPart={isEUVATCountry && !!isWithTaxes}
        />
        <SummaryPriceContainer
          isLoading={isCalculating}
          price={thousandSeperator(todaysCharge || 0)}
          name={t('common.today-charges')}
          hideSecondaryPart={isEUVATCountry && !!isWithTaxes}
        />
        {apiError && (
          <StyledErrorContainer>
            <StyledErrorIcon />
            {apiError}
          </StyledErrorContainer>
        )}
        <StyledButton
          id={buttonId}
          type={onClick ? 'button' : 'submit'}
          withoutMargin
          onClick={onClick}
          loading={isLoading || isCalculating}
          disabled={disabled || isCalculating}
          withoutDisabledStyles={!!withoutDisabledStyles}
          variant="pill"
        >
          <span className="p-button-label">{buttonText}</span>
        </StyledButton>
        <StyledPlanRenewText>
          {description || t('common.summary-renew')}
        </StyledPlanRenewText>
        <StyledContainer row>
          <StyledSafeChargeLogo src="assets/pci.svg" />
          <StyledSafeChargeLogo src="assets/ssl.svg" />
        </StyledContainer>
      </StyledSummaryContainer>
    );
  }
);
